import { type LinkProps } from '@ironhack/design-system2/components/link';

import {
  type DatoAllLocales,
  type DatoBlock,
  type DatoButtonBlock,
  type DatoCourse,
  type DatoIconBlock,
  type DatoImage,
  type DatoTargetBlock,
} from '@/lib/datocms/types';
import { type MenuItem } from './menu-item';

type NavbarButton = DatoButtonBlock & Pick<LinkProps, 'rel'>;

export type NavMenuColumn<
  Item extends Record<string, unknown> = Record<string, unknown>
> = {
  icon: DatoIconBlock | null;
  menuItems: Item[];
  subtitle: string | '';
  title: string;
};

export type NavbarTarget = DatoBlock<
  {
    eventLabel: string; // to be removed: currently present in datoCMS but not used
    target: DatoTargetBlock;
  },
  'NavbarTargetRecord'
>;

type NavBadge = {
  colorScheme: 'purple' | 'yellow';
  text: string;
} | null;

export type ThirdLevelItem = {
  badge: NavBadge;
  content: NavbarTarget;
  id: string;
  label: string;
};

export type ThirdLevelSection = {
  id: string;
  image: DatoImage | null;
} & NavMenuColumn<ThirdLevelItem>;

export type ThirdLevelContent = {
  sections: ThirdLevelSection[];
  eventLabel: string;
};

export type CourseMenuItem = {
  content: ThirdLevelContent | NavbarTarget;
  course: Pick<DatoCourse, 'code' | 'name'>;
  id: string;
};

type LabelMenuItem = {
  badge: NavBadge;
  content: ThirdLevelContent | NavbarTarget;
  id: string;
  label: string;
};

export type SecondLevelItem = CourseMenuItem | LabelMenuItem;

export type SecondLevelContent =
  | DatoBlock<
      NavMenuColumn<CourseMenuItem> & { eventLabel: string },
      'CourseMenuSecondLevelRecord'
    >
  | DatoBlock<
      NavMenuColumn<LabelMenuItem> & { eventLabel: string },
      'MenuSecondLevelRecord'
    >;

export type TopLevelItem = {
  content: SecondLevelContent | NavbarTarget;
  id: string;
  label: string;
};

export type DatoGeoRedirectSuggestion = {
  _allMessageLocales: DatoAllLocales[];
  _allRememberSelectionLocales: DatoAllLocales[];
};

export type NavbarQuery = {
  navbar: {
    button: [NavbarButton];
    fullApplyButton: [NavbarButton?];
    logoTarget: [DatoTargetBlock];
    menu: MenuItem; // Remove this once we delete NavbarMobile & NavbarDesktop
    navMenu: { menuItems: TopLevelItem[] };
  };
  geoRedirectSuggestion: DatoGeoRedirectSuggestion;
};

export const isTarget = (
  content: NavbarTarget | unknown
): content is NavbarTarget => Boolean((content as NavbarTarget).target);
